import {
  INDICATEURS_AFFICHES,
  type PointListe,
  STATUTS_TOUJOURS_AFFICHES,
  aRegarder,
  pointsDuStatut,
} from '@/lib/controle';
import { allerA as allerAvecEvidence } from '@/lib/mise-en-evidence';
import type { ControleNoyau } from '@rosetta/shared';
import { Fragment, type MouseEvent, useId } from 'react';
import { idCarteIndicateur } from './carte-indicateur';
import { CompteurPoints } from './compteur-points';

interface SyntheseControleProps {
  controle: ControleNoyau;
  /** The numbers of the indicators that have a card in the tab: their names are links to it. */
  avecCarte: ReadonlySet<string>;
  /**
   * Goes to the "Avant / après" tab (#23): the additions are marked in the translated text there,
   * so their name in the points to look at is followed by a link to it (their card, in the "Contenu"
   * theme since #30, is the link of their name).
   */
  onVoirAvantApres?: (() => void) | undefined;
}

/** The address of the "Avant / après" tab: the same page and noyau, the tab changed. */
function adresseAvantApres(): string {
  const recherche = new URLSearchParams(window.location.search);
  recherche.set('onglet', 'avant-apres');
  return `${window.location.pathname}?${recherche.toString()}`;
}

/** Brings a card into view and gives it the focus, so the keyboard goes on from there. */
function allerA(event: MouseEvent<HTMLAnchorElement>, id: string) {
  const carte = document.getElementById(id);
  if (carte === null) return;
  event.preventDefault();
  carte.scrollIntoView({ block: 'start' });
  carte.focus({ preventScroll: true });
}

/**
 * The summary of the control, above the text (A8): "Contrôle qualité", the points to look at
 * (the indicators "Bloquant", "À relire" or in error, by the names Tahiti gives them, each a link
 * to its card when it has one), and the counters of `compte_statuts`.
 */
export function SyntheseControle({ controle, avecCarte, onVoirAvantApres }: SyntheseControleProps) {
  const idTitre = useId();
  const points = aRegarder(controle);
  // "Bloquant", "À relire" and "OK" are always there (inactive when nothing leads anywhere); any
  // other status only when it counts something and a point of it has a card to go to (#30): the
  // screen says nothing it cannot lead to.
  const compteurs = (controle.comptes ?? [])
    .map(({ statut, nombre }) => ({
      statut,
      nombre,
      points: pointsDuStatut(controle, statut.code, avecCarte),
    }))
    .filter(
      ({ statut, nombre, points: pointsDuCompteur }) =>
        STATUTS_TOUJOURS_AFFICHES.has(statut.code) || (nombre > 0 && pointsDuCompteur.length > 0),
    );
  // A point of a counter takes the page to its card, which gets the focus (its name is its title).
  const aller = (point: PointListe) => {
    const carte = document.getElementById(idCarteIndicateur(point.numero));
    if (carte !== null) allerAvecEvidence(carte);
  };
  return (
    <section
      aria-labelledby={idTitre}
      className="flex items-center justify-between gap-6 rounded-md border border-gray-200 px-6 py-4"
    >
      <div className="min-w-0">
        <h2 id={idTitre} className="font-semibold text-body-lg leading-[1.3]">
          Contrôle qualité
        </h2>
        <p className="mt-1 text-gray-700 text-small leading-normal">
          {points.length === 0 ? (
            'Aucun point à regarder.'
          ) : (
            <>
              Points à regarder&nbsp;:{' '}
              {points.map((point, rang) => {
                const id = idCarteIndicateur(point.numero);
                return (
                  <Fragment key={point.numero}>
                    {rang === 0 ? null : ', '}
                    {avecCarte.has(point.numero) ? (
                      <a
                        href={`#${id}`}
                        onClick={(event) => allerA(event, id)}
                        className="font-semibold"
                      >
                        {point.nom}
                      </a>
                    ) : (
                      <span className="font-semibold text-black">{point.nom}</span>
                    )}
                    {point.numero === INDICATEURS_AFFICHES.ajouts && onVoirAvantApres ? (
                      <>
                        {' '}
                        (
                        <a
                          href={adresseAvantApres()}
                          className="font-semibold"
                          onClick={(event) => {
                            event.preventDefault();
                            onVoirAvantApres();
                          }}
                        >
                          voir Avant / après
                        </a>
                        )
                      </>
                    ) : null}
                  </Fragment>
                );
              })}
              .
            </>
          )}
        </p>
      </div>
      {compteurs.length === 0 ? null : (
        <ul aria-label="synthèse du contrôle" className="flex shrink-0 flex-wrap gap-2">
          {compteurs.map(({ statut, nombre, points: pointsDuCompteur }) => (
            <li key={statut.code}>
              <CompteurPoints
                statut={statut}
                nombre={nombre}
                points={pointsDuCompteur}
                onChoisir={aller}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
